import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface AdminActivity {
  id: string;
  user: string;
  action: string;
  content: string;
  time: string;
  type: 'topic' | 'post';
  ip_address?: string;
  topic_info?: {
    title: string;
    slug: string;
    category_slug: string;
  };
}

export const useAdminActivity = () => {
  return useQuery({
    queryKey: ['admin-activity'],
    queryFn: async () => {
      // Get recent topics with category information
      const { data: recentTopics, error: topicsError } = await supabase
        .from('topics')
        .select(`
          id,
          title,
          slug,
          created_at,
          author_id,
          categories (
            slug
          )
        `)
        .order('created_at', { ascending: false })
        .limit(5);

      if (topicsError) throw topicsError;

      // Get recent posts with topic and category information
      const { data: recentPosts, error: postsError } = await supabase
        .from('posts')
        .select(`
          id,
          content,
          created_at,
          author_id,
          topic_id,
          ip_address,
          topics (
            id,
            title,
            slug,
            categories (
              slug
            )
          )
        `)
        .order('created_at', { ascending: false })
        .limit(5);

      if (postsError) throw postsError;

      // Get unique author IDs - handle both user_id and author_id
      const topicUserIds = recentTopics?.map((topic: any) => topic.user_id || topic.author_id).filter(Boolean) || [];
      const postUserIds = recentPosts?.map((post: any) => post.user_id || post.author_id).filter(Boolean) || [];
      const authorIds = [...new Set([...topicUserIds, ...postUserIds])];

      // Fetch user data from profiles only
      const profilesData = authorIds.length > 0 ? await supabase
        .from('profiles')
        .select('id, display_name')
        .in('id', authorIds)
        .then(({ data }) => data || []) : [];

      // Create a map for quick user lookup
      const userMap = new Map();
      profilesData.forEach((profile: any) => {
        userMap.set(profile.id, profile.display_name || 'User');
      });

      // Combine and format activities
      const activities: AdminActivity[] = [];

      // Add topics
      recentTopics?.forEach((topic: any) => {
        const userId = topic.user_id || topic.author_id;
        const username = userId ? userMap.get(userId) || 'Anonymous User' : 'Anonymous User';
        activities.push({
          id: topic.id,
          user: username,
          action: 'Created topic',
          content: topic.title,
          time: topic.created_at,
          type: 'topic',
          ip_address: undefined,
          topic_info: {
            title: topic.title,
            slug: topic.slug,
            category_slug: topic.categories?.slug || ''
          }
        });
      });

      // Add posts
      recentPosts?.forEach((post: any) => {
        const userId = post.user_id || post.author_id;
        const username = userId ? userMap.get(userId) || 'Anonymous User' : 'Anonymous User';
        const topicTitle = post.topics?.title || 'Unknown Topic';
        activities.push({
          id: post.id,
          user: username,
          action: 'Replied to',
          content: topicTitle,
          time: post.created_at,
          type: 'post',
          ip_address: undefined,
          topic_info: post.topics ? {
            title: post.topics.title,
            slug: post.topics.slug,
            category_slug: post.topics.categories?.slug || ''
          } : undefined
        });
      });

      // Sort by time and take the 10 most recent
      return activities
        .sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime())
        .slice(0, 10);
    },
  });
};